import { useDispatch } from 'react-redux';
import React, { useCallback } from 'react';
import { identity, prop } from 'ramda';
import { useDeviceSelector, useGameConfigurationSelector, useUserSelector } from '@core/js/store/hooks';
import { ManagedPopup } from '@core/js/components/common/Popup';
import { Game as CommonGame } from '@core/js/components/common/Game';
import { WebContentInner } from '@core/js/components/system/WebContent';
import { DISPLAY_MODE, GAME_SIZES } from '@theme/js/components/common/GamesView/constants';
import { getOkButton } from '@core/js/components/common/popups/CommonPopup';
import GridPagingSlider from '@theme/js/components/common/GamesView/GridPagingSlider';
import { updatePopupAction, useOnClose } from '@core/js/components/common/PopupManager';
import { getAcceptDeclineButtons } from '@core/js/components/common/popups/ConfirmationPopup';
import { updatePeriod as updateBonusHistoryPeriod } from '@core/js/components/main/BonusHistory/store/actions';

import * as helpers from '@core/js/components/popups/BonusWithGamesPopup/helpers';
import { acceptBonusMessage } from '@core/js/components/popups/BonusWithGamesPopup/server';

import style from '@core/js/components/popups/BonusWithGamesPopup/BonusWithGamesPopup.scss';

const SLIDER_OPTIONS_MINI = {
    animation: {
        type: 'monoSlider'
    },
    controls: {
        arrows: { enabled: true },
        bullets: { enabled: false },
        buttons: { enabled: false }
    }
};
const Game = (props) => <CommonGame {...props} showTitle={true} />;

export const BonusWithGamesPopup = ({
    id,
    title,
    message,
    dialogId,
    controls,
    layoutClassName, /* Solverde changes */
    isAccepted,
    rawBonusDetails,
    isConfirmation,
    syncActionsOnClose = identity,
    useDescription = identity,
    showGamesList
}) => {
    const closeManagedPopup = useOnClose();
    const games = useGameConfigurationSelector(prop('games'));
    const lang = useUserSelector(prop('lang'));
    const bonusDetails = helpers.parseBonus({ data: rawBonusDetails, games, lang });
    const selectedGames = bonusDetails.bonusTemplate?.gamingContext?.selectedGames || [];
    const isModal = isConfirmation && !isAccepted;
    const dispatch = useDispatch();
    const portalDescription = useDescription();
    const isDesktop = useDeviceSelector(prop('isDesktop'));

    const handleClosePopup = useCallback(() => {
        closeManagedPopup();
        dispatch(updateBonusHistoryPeriod());
    }, [closeManagedPopup, dispatch]);

    const closeManagedAndSync = useCallback(() => {
        syncActionsOnClose();
        handleClosePopup();
    }, [handleClosePopup, syncActionsOnClose]);

    const onAcceptClick = useCallback(async () => {
        await acceptBonusMessage({ accept: true, dialogId });
        syncActionsOnClose();

        if (Number(showGamesList)) {
            dispatch(updatePopupAction({ id, isAccepted: true, isModal: false }));
        } else {
            handleClosePopup();
        }
    }, [dialogId, id, syncActionsOnClose, dispatch, handleClosePopup, showGamesList]);

    const onDeclineClick = useCallback(async () => {
        await acceptBonusMessage({ accept: false, dialogId });
        closeManagedAndSync();
    }, [closeManagedAndSync, dialogId]);

    const onOkClick = useCallback(() => {
        if (Number(showGamesList)) {
            dispatch(updatePopupAction({ id, isAccepted: true, isModal: false }));
        } else {
            handleClosePopup();
        }

    }, [id, dispatch, handleClosePopup, showGamesList]);

    /* Solverde changes */
    const layerClassNames = {
        layout: `${style.layout} ${layoutClassName || ''}`
    };
    /* Solverde changes */

    const acceptDeclineButtons = getAcceptDeclineButtons({ onAcceptClick, onDeclineClick });

    const buttons = isConfirmation && !isAccepted
        ? acceptDeclineButtons
        : getOkButton(isAccepted ? closeManagedAndSync : onOkClick);

        return (
            <ManagedPopup
                id={id}
                title={title}
                isModal={isModal}
                buttons={buttons}
                controls={controls}
                layerClassNames={layerClassNames} /* Solverde changes */
                onClose={handleClosePopup}
            >
                {isAccepted
                    ? <>
                        {portalDescription}
                        <GridPagingSlider
                            Game={Game}
                            swipe={!isDesktop}
                            gamesPerSlideMin={1}
                            fillLastSlide={false}
                            showFeatureIcons={false}
                            gameCodes={selectedGames}
                            showControlsOnHover={false}
                            displayMode={DISPLAY_MODE.PAGING}
                            gameSize={GAME_SIZES.EXTRA_SMALL}
                            classNames={{ game: style.game }}
                            sliderOptions={SLIDER_OPTIONS_MINI}
                        />
                    </>
                    : <WebContentInner content={message} />
                }
            </ManagedPopup>
        );
};
